import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Cpu, Award, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Counter } from "../components/Counter";
import { ClientCarousel } from "../components/ClientCarousel";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { SERVICES, STATS, INDUSTRIES } from "../data/site";

const HERO_IMG = "https://images.pexels.com/photos/37793901/pexels-photo-37793901.jpeg";

const HERO_LINES = [
  { text: "Engineering", cls: "text-white" },
  { text: "Safe & Reliable", cls: "text-brand-orange" },
  { text: "City Gas Infrastructure.", cls: "text-white" },
];

export default function HomePage({ onQuoteClick }) {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <div data-testid="home-page">
      <Seo
        title="IDEAL ENTERPRISES — City Gas Distribution & Pipeline Construction"
        description="End-to-end CGD pipeline construction across India: MDPE, steel, PNG, HDD, utility infrastructure with safety-first execution."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMG} alt="Engineers inspecting large gas pipelines at a construction site" className="w-full h-[115%] object-cover" />
          <div className="absolute inset-0 bg-[#0B2A4A]/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2A4A]/90 via-[#0B2A4A]/55 to-[#0B2A4A]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B2A4A]/40 via-transparent to-[#0B2A4A]/60" />
          <div className="absolute inset-0 blueprint-grid opacity-15" />
        </motion.div>

        <motion.div style={{ y: contentY, opacity: contentOpacity }} className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10 pt-32 pb-20 w-full">
          <div className="max-w-4xl">
            <motion.p
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="overline text-brand-orange"
            >
              EST. 1998 / PAN-INDIA OPERATIONS
            </motion.p>

            <h1 className="font-heading font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl mt-5 leading-[0.95]" data-testid="hero-headline">
              {HERO_LINES.map((l, i) => (
                <span key={i} className="block overflow-hidden pb-1 -mb-1">
                  <motion.span
                    className={`block ${l.cls}`}
                    initial={{ y: "112%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.95, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {l.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="text-white/80 text-lg md:text-xl mt-7 max-w-2xl leading-relaxed"
            >
              End-to-end City Gas Distribution (CGD) solutions — MDPE pipeline laying, steel pipeline construction, PNG connections, CNG infrastructure, HDD works, testing, commissioning, and utility infrastructure — with an uncompromising focus on safety, quality, and timely execution.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={onQuoteClick}
                data-testid="hero-quote-btn"
                className="group inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-7 py-4 text-sm font-semibold tracking-wide btn-lift hover:bg-orange-600"
              >
                Request a Quote
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-7 py-4 text-sm font-semibold tracking-wide hover:bg-white hover:text-brand-blue transition-colors"
              >
                Explore Services
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.1 }}
              className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 max-w-3xl border-t border-white/10 pt-8"
            >
              {STATS.slice(0, 4).map((s) => (
                <div key={s.label}>
                  <div className="font-mono-tech text-3xl md:text-4xl text-white font-semibold tabular-nums">
                    {s.value}{s.suffix}
                  </div>
                  <p className="text-white/60 text-xs mt-1 uppercase tracking-wider font-mono-tech">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 font-mono-tech text-[10px] tracking-[0.3em] z-10">
          SCROLL TO EXPLORE
        </div>
      </section>

      <EditorialMarquee />

      {/* ABOUT TEASER */}
      <section className="py-24 bg-white" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">About Ideal Enterprises</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-4 leading-tight">
              A trusted infrastructure contractor for India's CGD &amp; EPC ecosystem.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed">
              We deliver gas pipeline construction services for CGD companies, oil &amp; gas operators, and EPC contractors across India. Our practice combines an experienced site workforce, qualified engineering leadership, modern fusion / welding equipment, and a safety culture that doesn't make exceptions.
            </p>
            <Link
              to="/about"
              data-testid="about-teaser-cta"
              className="inline-flex items-center gap-2 mt-8 text-brand-blue font-semibold border-b-2 border-brand-orange pb-1 hover:text-brand-orange"
            >
              Learn more about us <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-slate-200">
            {[
              { Icon: ShieldCheck, t: "Safety-first culture", d: "Zero compromise on HSE protocols on every site, every shift." },
              { Icon: Cpu, t: "Modern equipment", d: "CNC fusion machines, 24T+ HDD rigs, calibrated pressure rigs." },
              { Icon: Award, t: "Quality assurance", d: "ITP-driven hold points with full TPI / PMC interface." },
              { Icon: Clock, t: "On-time delivery", d: "Documented track record of early or on-schedule completion." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="bg-white p-8 hover:bg-slate-50 transition-colors group">
                <Icon size={28} className="text-brand-orange" />
                <h3 className="font-heading font-bold text-lg text-brand-blue mt-5">{t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24 bg-[#F8F9FA] border-y border-slate-200" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="overline">What We Build</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-3 max-w-2xl">
                14 service verticals across the CGD &amp; utility value chain.
              </h2>
            </div>
            <Link to="/services" data-testid="services-view-all" className="text-sm font-semibold text-brand-orange hover:underline inline-flex items-center gap-1">
              View all services <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 grid-borders border-t border-l border-slate-200 bg-white">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                >
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group block p-7 h-full hover:bg-slate-50 transition-colors relative"
                  >
                    <span className="absolute top-0 left-0 right-0 h-0.5 bg-brand-orange scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
                    <span className="font-mono-tech text-[11px] text-slate-400">0{(i+1).toString().padStart(2, "0")}</span>
                    <Icon size={28} className="text-brand-blue group-hover:text-brand-orange transition-colors mt-3" />
                    <h3 className="font-heading font-bold text-lg text-brand-blue mt-5">{s.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.short}</p>
                    <span className="inline-flex items-center gap-1 mt-5 text-xs font-semibold text-brand-orange opacity-0 group-hover:opacity-100 transition-opacity">
                      Read more <ArrowRight size={12} />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US — COUNTERS */}
      <section className="py-24 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="overline">Why Choose Us</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-3">
              The numbers that define our discipline.
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              A snapshot of how we deliver — from workforce scale to documented kilometres of executed pipeline, and an HSE record we are proud to share with prospective partners.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 grid-borders border-t border-l border-slate-200">
            {STATS.map((s) => (
              <div key={s.label} className="p-7">
                <p className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-400">{s.unit}</p>
                <div className="mt-3"><Counter value={s.value} suffix={s.suffix} /></div>
                <p className="text-sm text-slate-700 mt-2 font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ON-GROUND PROJECT WORK */}
      <section className="py-24 bg-brand-blue text-white relative overflow-hidden" data-testid="project-work">
        <div className="absolute inset-0 blueprint-grid opacity-10" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="overline text-brand-orange">On-Ground Project Work</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mt-3 leading-tight">
                Currently executing: GAIL Pradhanmantri Urja Ganga Yojana, Varanasi.
              </h2>
              <p className="text-white/70 mt-5 leading-relaxed">
                Real site photographs from our live package with GAIL (India) Limited — M/s Ideal Enterprises is the executing contractor on the city gas distribution rollout in Varanasi, Uttar Pradesh.
              </p>
            </div>
            <div className="font-mono-tech text-xs tracking-wider text-white/60 border border-white/20 px-4 py-3 self-start">
              CLIENT · GAIL (INDIA) LIMITED<br />
              LOCATION · VARANASI, UP<br />
              PROJECT · PM URJA GANGA YOJANA
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                src: "/projects/varanasi-board.jpg",
                tag: "PROJECT SIGNAGE",
                caption: "Official project board — GAIL (India) Limited, Contractor: M/s Ideal Enterprises, Chetganj, Varanasi.",
                span: "",
              },
              {
                src: "/projects/crew-night-1.jpg",
                tag: "SITE CREW",
                caption: "Our pipeline crew on a live night-shift section of the Varanasi package.",
                span: "",
              },
              {
                src: "/projects/crew-night-2.jpg",
                tag: "SITE MOBILISATION",
                caption: "Team briefing and tool mobilisation ahead of underground PE pipeline works.",
                span: "",
              },
            ].map((p, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative overflow-hidden border border-white/10 group"
                data-testid={`project-photo-${i}`}
              >
                <div className="aspect-[4/5] md:aspect-[4/5] overflow-hidden">
                  <img
                    src={p.src}
                    alt={p.caption}
                    loading="lazy"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent pointer-events-none" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="font-mono-tech text-[10px] tracking-[0.25em] text-brand-orange">{p.tag}</span>
                  <p className="text-white text-sm font-medium mt-2 leading-snug">{p.caption}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="overline">Industries Served</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-3">
              Below-ground expertise for the sectors building tomorrow's India.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 grid-borders border-t border-l border-slate-200">
            {INDUSTRIES.map((ind) => {
              const Icon = ind.icon;
              return (
                <div key={ind.name} className="p-8 group hover:bg-slate-50 transition-colors" data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <Icon size={32} className="text-brand-blue group-hover:text-brand-orange transition-colors" />
                  <h3 className="font-heading font-bold text-xl text-brand-blue mt-5">{ind.name}</h3>
                  <p className="text-sm text-slate-600 mt-2">{ind.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <ClientCarousel />

      {/* CTA STRIP */}
      <section className="py-20 bg-brand-orange" data-testid="cta-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-white tracking-tight">
              Have a CGD or utility package coming up?
            </h2>
            <p className="text-white/90 mt-2">Talk to our engineering team. We'll respond within one business day.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onQuoteClick}
              data-testid="cta-strip-quote"
              className="bg-brand-blue text-white px-7 py-4 font-semibold btn-lift hover:bg-brand-blue-dark"
            >
              Request a Quote
            </button>
            <Link
              to="/contact"
              data-testid="cta-strip-contact"
              className="border border-white/70 text-white px-7 py-4 font-semibold hover:bg-white hover:text-brand-orange transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
